import React from 'react';
import { CurveType, LineChart, lineDataItem, yAxisSides } from 'react-gifted-charts';
import { XStack, YStack, Text } from 'tamagui';
import { tokens } from 'tamagui-config/src/tokens';

const CHART_WIDTH = 480;
const CHART_HEIGHT = 140;
const CHART_THICKNESS = 5;
const INITIAL_SPACING = 0;
const END_SPACING = 25;
const START_OPACITY = 0.3;
const END_OPACITY = 0;
const NO_OF_SECTIONS = 3;
const Y_AXIS_OFFSET_DELTA = 1;
const HORIZONTAL_RULES_OPACITY = 0.12;

export interface HubLineChartProps {
  data: lineDataItem[];
  unit: string;
  minValue?: number;
  maxValue?: number;
  chartHeight?: number;
  noOfSections?: number;
  color?: string;
  lineGradientStartColor?: string;
  lineGradientEndColor?: string;
  startFillColor?: string;
  endFillColor?: string;
}

export const HubLineChart: React.FC<HubLineChartProps> = (props) => {
  const { data: validData } = props;

  const renderXAxis = () =>
    validData.map((value, index) => (
      <Text fontSize={24} color={tokens.color.textLight} key={index} opacity={value.value === null ? 0.3 : 1}>
        {value.label}
      </Text>
    ));

  if (validData.length === 0) {
    return (
      <YStack
        height={props.chartHeight ?? CHART_HEIGHT}
        justifyContent="center"
        alignItems="center">
        <Text fontSize={24} color={tokens.color.textLight}>
          No data available
        </Text>
      </YStack>
    );
  }

  const values = validData.map(({ value }) => value !== null && value !== undefined ? value : 0);
  const minChartValue = props.minValue ?? Math.round(Math.min(...values)) - Y_AXIS_OFFSET_DELTA;
  let maxChartValue = props.maxValue ?? Math.round(Math.max(...values)) + Y_AXIS_OFFSET_DELTA;
  // for small differences, get at least NO_OF_SECTIONS chart height
  if (props.maxValue === undefined && maxChartValue - minChartValue < NO_OF_SECTIONS) {
    maxChartValue = minChartValue + NO_OF_SECTIONS;
  }

  return (
    <YStack>
      <LineChart
        width={CHART_WIDTH}
        height={props.chartHeight ?? CHART_HEIGHT}
        adjustToWidth
        data={validData.map(({ value }) => ({ value }))}
        curved
        areaChart
        hideDataPoints
        horizontalRulesStyle={{ opacity: HORIZONTAL_RULES_OPACITY }}
        curveType={CurveType.QUADRATIC}
        startOpacity={START_OPACITY}
        endOpacity={END_OPACITY}
        interpolateMissingValues={false}
        pointerConfig={{ showPointerStrip: false, hidePointers: true }}
        thickness={CHART_THICKNESS}
        initialSpacing={INITIAL_SPACING}
        endSpacing={END_SPACING}
        color={props.color || tokens.color.hgPrimaryGradientStart}
        lineGradientStartColor={props.lineGradientStartColor || tokens.color.hgPrimaryGradientStart}
        startFillColor={props.startFillColor || tokens.color.hgPrimaryGradientStart}
        lineGradientEndColor={props.lineGradientEndColor || tokens.color.hgPrimaryGradientEnd}
        endFillColor={props.endFillColor || tokens.color.hgPrimaryGradientEnd}
        yAxisTextStyle={{ color: tokens.color.textLight, fontSize: 24 }}
        yAxisSide={yAxisSides.RIGHT}
        yAxisLabelSuffix={props.unit}
        yAxisOffset={minChartValue}
        yAxisExtraHeight={15}
        roundToDigits={0}
        maxValue={maxChartValue - minChartValue}
        yAxisIndicesColor={'transparent'}
        xAxisColor="transparent"
        yAxisColor="transparent"
        rulesType="solid"
        noOfSections={props.noOfSections ?? NO_OF_SECTIONS}
        backgroundColor="transparent"
        lineGradientDirection="horizontal"
      />
      <XStack width={CHART_WIDTH} justifyContent="space-between">
        {renderXAxis()}
      </XStack>
    </YStack>
  );
};
